/**
 * Synchronous token snapshot for the `ApiClient`'s region ROUTER.
 *
 * Region routing (region/continent host swap + minute failover) is decided
 * per-request and SYNCHRONOUSLY inside `ApiClient.resolveUrl`, but the session
 * (and therefore the decoded token carrying `region_config` / `continent`) only
 * becomes available asynchronously after Auth0 resolves. This tiny module
 * bridges the two: the session layer pushes the current decoded token here as
 * soon as it loads, and the router pulls it back synchronously.
 *
 * It is intentionally a module singleton (not React state) because the
 * `ApiClient` is created once at bootstrap, before React renders.
 *
 * This is a SNAPSHOT, taken once per page load, and it is not the credential.
 * The bearer comes from `SessionManager.getAccessToken()`, which re-mints an
 * expired token off the refresh token; authenticating from the `__raw` in here
 * is why a tab left open used to 403 on everything until it was reloaded, while
 * a more recently opened tab (holding a newer snapshot) carried on working.
 * `ApiClient` only falls back to `__raw` when there is no live token at all.
 */
import type { AutheToken } from "@authe/fe-commons/api";

let current: AutheToken | null = null;

/** Push the live session's decoded token (or `null` on sign-out). */
export function setRoutingToken(token: AutheToken | null): void {
  current = token;
}

/** Current decoded token for region routing, or `null` before the session loads. */
export function getRoutingToken(): AutheToken | null {
  return current;
}
